import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { useAuth } from "context/auth-context";
import { SEEDED_LOGIN_GROUPS } from "constants/seed-login-users";
import { LoginValueProps } from "types";
import userAuthService from "services/sub-services/user-auth-service/user-auth.service";
import { AuthLayout } from "components/layouts/auth-layout/auth-layout";
import { LoginForm } from "components/molecules/forms/login/login-form";
import { canAccessPortalPath, getAccessiblePortalPath } from "utils/portal-access";
import { LoginContentStack, LoginSection } from "pages/auth/login/styles/login.page.inline.styles";

type LoginLocationState = {
  from?: {
    pathname?: string;
    search?: string;
    hash?: string;
  };
};

export const LoginPage = () => {
  const history = useHistory();
  const location = useLocation<LoginLocationState>();
  const { login } = useAuth();
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const timeout = setTimeout(() => setAnimate(true), 100);
    return () => clearTimeout(timeout);
  }, []);

  const initialValues = {
    username: "",
    password: "",
  };

  const onLoginSubmit = async (formData: LoginValueProps) => {
    const session = await userAuthService.loginService(formData);
    const portalAccess = session.user?.portal_access || null;
    const requestedPath = location.state?.from?.pathname;
    const requestedSearch = location.state?.from?.search || "";
    const requestedHash = location.state?.from?.hash || "";
    const nextPath =
      requestedPath && canAccessPortalPath(requestedPath, portalAccess)
        ? `${requestedPath}${requestedSearch}${requestedHash}`
        : getAccessiblePortalPath(portalAccess);

    login({
      accessToken: session.accessToken,
      refreshToken: session.refreshToken,
      role: session.user?.role || null,
      portalAccess,
    });
    history.replace(nextPath);
  };

  const onSuccess = () => undefined;

  return (
    <AuthLayout>
      <LoginSection $animate={animate}>
        <LoginContentStack>
          <LoginForm
            initialValues={initialValues}
            onSuccess={onSuccess}
            quickLoginGroups={SEEDED_LOGIN_GROUPS}
            submitForm={onLoginSubmit}
          />
        </LoginContentStack>
      </LoginSection>
    </AuthLayout>
  );
};

export default LoginPage;
